/**
 * User risk scoring — pure, deterministic, testable.
 *
 * Inputs are the JumpCloud user object plus a precomputed `hasMFA` flag
 * (because MFA data may live in a separate MFAStatus payload).
 *
 * Returns 0-100 risk points and a qualitative level:
 *   level 'low'    when points < 20
 *   level 'medium' when 20 <= points < 50
 *   level 'high'   when points >= 50
 *
 * The scoring is accumulative: each risk factor adds points, capped per
 * factor. The factors are documented inline so the score is explainable
 * to the client ("why is this user high risk?").
 */

import type { JumpCloudUser } from '../types'

export type RiskLevel = 'low' | 'medium' | 'high'

export interface RiskFactor {
  key: string
  points: number
  label: string
}

export interface UserRisk {
  score: number       // 0-100
  level: RiskLevel
  factors: RiskFactor[]
}

export interface UserRiskInput {
  user: JumpCloudUser
  hasMFA: boolean
  mfaType?: 'totp' | 'webauthn' | 'push' | 'sms' | 'none'
  now?: number        // injectable for tests
}

const DAY = 24 * 60 * 60 * 1000

function safeDate(value?: string): number | null {
  if (!value) return null
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? t : null
}

export function calculateUserRisk(input: UserRiskInput): UserRisk {
  const { user, hasMFA, mfaType } = input
  const now = input.now ?? Date.now()
  const factors: RiskFactor[] = []

  // 1. MFA — the single most important control.
  if (!hasMFA) {
    factors.push({ key: 'no-mfa', points: 40, label: 'No MFA enrolled' })
  } else if (mfaType === 'sms') {
    factors.push({ key: 'weak-mfa', points: 10, label: 'Weak MFA (SMS)' })
  }

  // 2. Account lockout (signals credential attack or forgotten password).
  if (user.account_locked) {
    factors.push({ key: 'locked', points: 20, label: 'Account locked' })
  }

  // 3. Password hygiene.
  if (user.password_expired) {
    factors.push({ key: 'pwd-expired', points: 15, label: 'Password expired' })
  } else if (!user.password_never_expires) {
    const exp = safeDate(user.password_expiration_date)
    if (exp != null) {
      const diff = exp - now
      if (diff > 0 && diff <= 14 * DAY) {
        factors.push({ key: 'pwd-expiring', points: 5, label: 'Password expires in 14 days' })
      }
    }
  }

  // 4. Dormant account (active but unused = attack surface).
  if (user.activated && !user.suspended) {
    const lastLogin = safeDate(user.lastLogin)
    if (lastLogin == null) {
      factors.push({ key: 'never-logged-in', points: 10, label: 'Never logged in' })
    } else if (now - lastLogin > 90 * DAY) {
      factors.push({ key: 'inactive-90d', points: 10, label: 'Inactive for 90+ days' })
    }
  }

  // 5. Stale account that was never activated — lower weight, still worth flagging.
  if (!user.activated && !user.suspended) {
    factors.push({ key: 'not-activated', points: 5, label: 'Account not activated' })
  }

  // Sum, cap at 100.
  const score = Math.min(100, factors.reduce((sum, f) => sum + f.points, 0))
  const level: RiskLevel = score >= 50 ? 'high' : score >= 20 ? 'medium' : 'low'

  return { score, level, factors }
}
